import React, { useState, useMemo } from "react";
import {
  Modal,
  Button,
  Col,
  Row
} from "reactstrap";
import styled from "styled-components";
import { useQuery } from "@apollo/react-hooks";
import { compose, graphql } from "react-apollo";
import {
  Subtitle,
  UpperTitle,
  ModalCard,
  Box,
  ErrorBox,
  ErrorText,
  ErrorTitle,
} from "../../../components/StyledComponents";

import { withSnackbarConsumer, Color } from "../../../contexts/SnackbarContext/SnackbarContext.jsx";
import { httpClient } from "../../../settings/apolloClient";
import InfoSingleCard from "../../../components/Infos/InfoSingleCard";
import InfoSimpleCard from "../../../components/Infos/InfoSimpleCard";
import { ShipmentInfo } from "../../../components/ShipmentInfo/ShipmentInfo.jsx";
import { FormulaInfo } from "../../../components/FormulaInfo/FormulaInfo.jsx";
import { FormulaMaterialTable } from "../../../components/FormulaMaterialTable/FormulaMaterialTable.jsx";

import Checkbox from "../../../components/ConfirmModal/Checkbox.jsx";

import { CONFIRM_SHIPMENT_REQUEST_MUTATION } from "./graphql/mutations/confirmShipmentRequestMutation";
import { ABORT_WAITING_SHIPMENT_REQUEST } from "./graphql/mutations/abortWaitingShipmentRequests.js";
import { HUMIDITY_QUERY } from "./graphql/queries/humidityQuery";

import { useMobileDevice } from "../../../utils/getDeviceType";
import WaterQuantity from "./WaterQuantity";
import { SlidersContainer } from "./SlidersContainer.jsx";

const Container = styled.div`
  padding: 0 30px 30px 30px;
`;

const CodeInfo = ({
  request,
}) => {
  const {
    code,
  } = request || {};

  const item = {
    label: "Código do Carregamento",
    value: code,
    unit: null,
  };

  return <InfoSingleCard item={item} />;
};

const VolumeInfo = ({
  request,
  setParams,
}) => {
  const {
    quantity,
  } = request || {};

  const item = {
    label: "Volume",
    value: quantity,
    unit: "m³",
  };

  const handleChangeValue = (value) => {
    setParams((prev) => ({
      ...prev,
      quantity: value,
    }));
  };

  return (
    <React.Fragment>
      <InfoSingleCard
        item={item}
        setValue={handleChangeValue}
        editable={!quantity}
      />
    </React.Fragment>
  );
};

const SealInfo = ({
  request,
  setParams,
}) => {
  const {
    sealNumber,
  } = request || {};

  const item = {
    label: "Lacre",
    value: sealNumber,
  };

  const handleChangeValue = (value) => {
    setParams((prev) => ({
      ...prev,
      sealNumber: value,
    }));
  };

  return (
    <React.Fragment>
      <InfoSimpleCard
        item={item}
        setValue={handleChangeValue}
        editable={!sealNumber}
      />
    </React.Fragment>
  );
};

const ErrorMessage = ({
  message
}) => (
  <ErrorBox>
    <ErrorTitle>ATENÇÃO!</ErrorTitle>
    <ErrorText bold>A carga não iniciou automaticamente devido a um ou mais problemas listados abaixo:</ErrorText>
    <ErrorText>{message}</ErrorText>
  </ErrorBox>
);

const ShipmentRequestModal = ({
  isOpen,
  setIsOpen,
  inventoryItems,
  inventoryLocations,
  shipmentRequest,
  snackbarContext,
  confirmShipmentRequest,
  abortWaitingShipmentRequests,
}) => {
  const isMobile = useMobileDevice();
  // const [locationItemLink, setLocationItemLink] = useState({});
  const [waterQuantity, setWaterQuantity] = useState(0);

  const sands = useMemo(() => {
    if (inventoryItems) {
      return inventoryItems.filter(({ materialType }) => materialType === "SAND") || [];
    }
  }, [inventoryItems]);

  const water = useMemo(() => {
    if (inventoryItems) {
      return inventoryItems.find(({ materialType }) => materialType === "WATER");
    }
  }, [inventoryItems]);

  const { data: humidityData } = useQuery(HUMIDITY_QUERY, {
    fetchPolicy: "network-only",
    client: httpClient,
  });

  const {
    currentHumidityMeasurements,
  } = humidityData || {};

  const humidityByItemCode = useMemo(() => {
    if (sands && currentHumidityMeasurements) {
      return sands.reduce((acc, sand) => {
        const match = currentHumidityMeasurements.find(({ inventoryItem }) => inventoryItem && inventoryItem.code === sand.code);
        acc[sand.code] = match ? match.value : 0;
        return acc;
      }, {});
    }
    return {};
  }, [sands, currentHumidityMeasurements]);

  const {
    waterCut,
    statusMessage,
  } = shipmentRequest || {};

  const [newWaterCut, setNewWaterCut] = useState(waterCut || 0);

  const cleanedMaterials = useMemo(() => {
    if (shipmentRequest && shipmentRequest.materials && shipmentRequest.materials.length === 0) return [];

    return shipmentRequest.materials
      .filter((material) => {
        const hasMaterialCode = material.materialCode != null;
        const hasAtLeastOneLocation = Array.isArray(material.locations) && material.locations.some(loc => loc.locationCode != null);

        return hasMaterialCode || hasAtLeastOneLocation;
      })
      .map((material) => ({
        ...material,
        humidity: material.humidity || humidityByItemCode[material.materialCode] || 0,
        locations: material.locations || [],
      }));
  }, [shipmentRequest, humidityByItemCode]);

  const [params, setParams] = useState({
    ...shipmentRequest,
    materials: cleanedMaterials,
  });

  // const linkLocationItem = (items, locations) => {
  //   const map = {};

  //   locations.forEach((loc) => {
  //     const matchedItem = items.find((item) => item.inventoryLocations.some((invLoc) => invLoc.code === loc.code));

  //     map[loc.code] = matchedItem.code || null;
  //   });

  //   return map;
  // };

  const onChangeSlider = (number) => {
    const wc = ((100 - parseFloat(number))) / 100 * waterQuantity;
    setNewWaterCut(wc);
    setParams((prev) => ({
      ...prev,
      waterCut: wc,
    }));
  };

  // useEffect(() => {
  //   if (inventoryItems && inventoryLocations) {
  //     setLocationItemLink(
  //       linkLocationItem(inventoryItems, inventoryLocations)
  //     );
  //   }
  // }, [inventoryItems, inventoryLocations]);

  const onChangeSandSlider = (itemCode, value) => {
    setParams((prev) => ({
      ...prev,
      materials: prev.materials.map((material) => (
        material.materialCode === itemCode
          ? { ...material, humidity: (value / 100) }
          : material
      )),
    }));
  };

  const handleChangeProofBody = () => {
    setParams((prev) => ({
      ...prev,
      proofBody: !params.proofBody,
    }));
  };

  const handleConfirmShipmentRequest = async () => {
    const {
      cementType,
      cep,
      code,
      constructionAddress,
      constructionName,
      contractCode,
      driver,
      equipmentCode,
      equipmentPlate,
      fck,
      formulaCode,
      gravelType,
      id,
      loadingStationCode,
      materials: materialsParam,
      operatorName,
      proofBody,
      quantity,
      sealNumber,
      slump,
      waterCut: waterCutParam,
    } = params;

    const cleaned = materialsParam.map(({
      humidity,
      materialCode,
      order,
      supplier,
      locations
    }) => {
      const cleanedLocations = (locations || [])
        .filter(loc => loc.locationCode && loc.weight)
        .map(({ locationCode, weight }) => ({ locationCode, weight }));

      return {
        humidity,
        materialCode,
        order,
        supplier,
        locations: cleanedLocations,
      };
    }).filter(item => item.materialCode && item.locations.length > 0);

    const cleanedDriver = driver ? {
      cpf: driver.cpf,
      name: driver.name,
      phoneNumber: driver.phoneNumber,
      licenseNumber: parseInt(driver.licenseNumber, 10),
      licenseExpirationDate: driver.licenseExpirationDate,
    } : null;

    confirmShipmentRequest({
      variables: {
        params: {
          cementType,
          cep,
          code,
          constructionAddress,
          constructionName,
          contractCode,
          driver: cleanedDriver,
          equipmentCode,
          equipmentPlate,
          fck: parseInt(fck, 10),
          formulaCode,
          gravelType,
          id,
          loadingStationCode,
          materials: cleaned,
          operatorName,
          proofBody,
          quantity,
          sealNumber,
          slump,
          waterCut: waterCutParam,
        },
      }
    }).then(() => {
      snackbarContext.setNotificationTimeOut(
        Color.success,
        "Sucesso."
      );
      setIsOpen(false);
    }).catch((error) => {
      snackbarContext.setNotificationTimeOut(
        Color.danger,
        `Erro ao iniciar carregamento. ${error.message}`
      );
    });
  };

  const handleAbortRequest = async () => {
    abortWaitingShipmentRequests().then(() => {
      snackbarContext.setNotificationTimeOut(
        Color.success,
        "Requisição abortada."
      );
      setIsOpen(false);
    }).catch((error) => {
      snackbarContext.setNotificationTimeOut(
        Color.danger,
        `Erro ao abortar requisição. ${error.message}`
      );
    });
  };

  return (
    <React.Fragment>
      <Modal
        isOpen={isOpen}
        // toggle={() => {
        //   setIsOpen(false);
        // }}
        size="lg"
        zIndex={2060}
      >
        <div className="modal-header justify-content-center" style={{ display: "block", border: "none" }}>
          <div style={{
            display: "flex", flexDirection: "row", justifyContent: "flex-end", alignItems: "center",
          }}
          >
            <UpperTitle>Carregamento recepcionado com restrições</UpperTitle>
            <button
              aria-label="Close"
              className="close"
              data-dismiss="modal"
              type="button"
              onClick={() => handleAbortRequest()}
            >
              <i className="nc-icon nc-simple-remove" />
            </button>
          </div>
          <Subtitle>Confirme com atenção as informações para iniciar o carregamento</Subtitle>
        </div>

        <Row noGutters>
          <Box
            style={{
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
              margin: "0 15px 30px 15px"
            }}
          >
            <Col
              lg={6}
              style={{
                borderRight: !isMobile ? "2px solid #fff" : "none"
              }}
            >
              <ModalCard>
                <CodeInfo request={shipmentRequest} />
              </ModalCard>
            </Col>
            <Col lg={6}>
              <ModalCard>
                <VolumeInfo
                  request={shipmentRequest}
                  setParams={setParams}
                />
              </ModalCard>
            </Col>
          </Box>
        </Row>

        <ShipmentInfo
          request={shipmentRequest}
          setParams={setParams}
        />

        <FormulaInfo
          request={shipmentRequest}
          setParams={setParams}
        />

        {sands && params && (
          <SlidersContainer
            inventoryItems={sands}
            onChangeSlider={onChangeSandSlider}
            params={params}
          />
        )}

        {params && cleanedMaterials && humidityByItemCode && (
          <FormulaMaterialTable
            request={shipmentRequest}
            setParams={setParams}
            // locationItemLink={locationItemLink}
            inventoryItems={inventoryItems}
            inventoryLocations={inventoryLocations}
            params={params}
            water={water}
            sands={sands}
            waterCut={newWaterCut}
            setWaterQuantity={setWaterQuantity}
          />
        )}

        <WaterQuantity
          waterQuantity={waterQuantity}
          onChangeSlider={onChangeSlider}
          waterCut={newWaterCut}
        />

        <SealInfo
          request={shipmentRequest}
          setParams={setParams}
        />

        <Container>
          <Checkbox
            value={params.proofBody}
            onChange={handleChangeProofBody}
            label="Moldar corpo de prova"
          />
        </Container>

        {statusMessage && <ErrorMessage message={statusMessage} />}

        <Button
          color="info"
          // disabled={statusMessage}
          onClick={handleConfirmShipmentRequest}
          style={{
            width: "100%",
            margin: "0",
            padding: "17px 16px",
            borderRadius: "0 0 10px 10px"
          }}
          // disabled={!enableStart}
        >
          Iniciar Carregamento
        </Button>
      </Modal>
    </React.Fragment>
  );
};

export default compose(
  graphql(CONFIRM_SHIPMENT_REQUEST_MUTATION, { name: "confirmShipmentRequest" }),
  graphql(ABORT_WAITING_SHIPMENT_REQUEST, { name: "abortWaitingShipmentRequests" }),
  withSnackbarConsumer,
)(ShipmentRequestModal);
